import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  useClaims,
  useUpdateClaimNumber,
  useUpdateClaimStatus,
  useUpdateClaimAmounts,
  useNotifyCustomer,
  useMarkPaymentReceived,
  useMarkWorkshopPaid,
  useMarkCustomerPaid,
  useSettleClaim,
  useCloseCase,
} from "../../../hooks/useWorkshopQueries";
import {
  Badge, PageHeader, Spinner, SearchInput, EmptyState, ErrorState,
  MetricCard, Modal, Avatar,formatDate, formatINR,
} from "../../../Component/Workshop/UI";
import AmountModal, { AMOUNT_FORM_DEFAULT, openAmountModal } from "./AmountModal";
import ClaimDetailModal from "./ClaimDetailModal";

// ─── Workflow config ──────────────────────────────────────────────────────────
export const STATUS_CFG = {
  NO_CLAIM:           { label:"No Claim",          variant:"gray",   step:0 },
  CLAIM_NUMBER_ADDED: { label:"Claim No. Added",   variant:"blue",   step:1 },
  UNDER_REVIEW:       { label:"Under Review",      variant:"yellow", step:2 },
  APPROVED:           { label:"Approved",          variant:"teal",   step:3 },
  REJECTED:           { label:"Rejected",          variant:"red",    step:0 },
  PAYMENT_REQUESTED:  { label:"Payment Requested", variant:"blue",   step:4 },
  PAYMENT_RECEIVED:   { label:"Payment Received",  variant:"teal",   step:5 },
  SETTLED:            { label:"Settled",           variant:"green",  step:6 },
  CASE_CLOSED:        { label:"Case Closed",       variant:"purple", step:7 },
};

export const STATUS_TABS = [
  { key:"ALL",               label:"All"            },
  { key:"NO_CLAIM",          label:"No Claim"       },
  { key:"CLAIM_NUMBER_ADDED",label:"Claim Added"    },
  { key:"UNDER_REVIEW",      label:"Under Review"   },
  { key:"APPROVED",          label:"Approved"       },
  { key:"REJECTED",          label:"Rejected"       },
  { key:"PAYMENT_RECEIVED",  label:"Paid"           },
  { key:"CASE_CLOSED",       label:"Closed"         },
];

export function customerPaymentDone(c) {
  if (c.customerDue === 0 || c.customerDue == null) return true; // NIL — nothing owed
  return c.customerPaymentStatus === "PAID" || c.customerPaid === true;
}
 

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ClaimsPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const globalSearch = searchParams.get("q") || "";

  const [activeTab,   setActiveTab]   = useState("ALL");
  const [search, setSearch] = useState(globalSearch);
  const [isTyping, setIsTyping] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [insFilter,   setInsFilter]   = useState("");

  const handleSearchChange = (value) => {
    setSearch(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value?.trim()) next.set("q", value);
        else next.delete("q");
        return next;
      },
      { replace: true }
    );
  };

  // Modals
  const [detailClaim,    setDetailClaim]    = useState(null);
  const [claimNoModal,   setClaimNoModal]   = useState(null);
  const [statusModal,    setStatusModal]    = useState(null);
  const [amountModal,    setAmountModal]    = useState(null);
  const [notifyModal,    setNotifyModal]    = useState(null);
  const [rejectModal,    setRejectModal]    = useState(null);
  const [payRecvModal,   setPayRecvModal]   = useState(null);
  const [settleModal,    setSettleModal]    = useState(null);
  const [closeModal,     setCloseModal]     = useState(null);

  // Form states
  const [claimNoForm,    setClaimNoForm]    = useState({ claimNumber:"" });
  const [statusForm,     setStatusForm]     = useState({ status:"", note:"", approvedAt:"", rejectionReason:"" });
  const [amountForm, setAmountForm]         =  useState(AMOUNT_FORM_DEFAULT);
  const [notifyForm,     setNotifyForm]     = useState({ channel:"EMAIL", customNote:"" });
  const [payRecvForm,    setPayRecvForm]    = useState({ receivedAmount:"", note:"", workshopPaid:false });
  const [payWorkshopModal, setPayWorkshopModal] = useState(null);
  const [custPayModal,     setCustPayModal]     = useState(null); 
   const [custPayForm,     setCustPayForm]      = useState({ amount:"", mode:"CASH", note:"" }); 
  const [payWorkshopForm,  setPayWorkshopForm]  = useState({ amount: "", note: "" });
  const [settleForm,     setSettleForm]     = useState({ settlementAmount:"", settlementDate:"", remarks:"" });
  const [closeForm,      setCloseForm]      = useState({ closingRemarks:"" });

    useEffect(() => {
      setIsTyping(true);
      const timer = setTimeout(() => {
        setDebouncedSearch(search);
        setIsTyping(false);
    }, 500);

      return () => clearTimeout(timer);
    }, [search]);

  useEffect(() => {
    if (globalSearch !== search) setSearch(globalSearch);
  }, [globalSearch]);

  // ── Real API query ────────────────────────────────────────────────────────
  const queryFilters = {};
  if (activeTab !== "ALL") queryFilters.status    = activeTab;
  if (insFilter)           queryFilters.insurance = insFilter;
  if (debouncedSearch)     queryFilters.search    = debouncedSearch;

  const { data, isLoading, isError, refetch } = useClaims(queryFilters);
  const updateClaimNoMutation = useUpdateClaimNumber();
  const updateStatusMutation  = useUpdateClaimStatus();
  const updateAmountsMutation = useUpdateClaimAmounts();
  const notifyMutation        = useNotifyCustomer();
  const payRecvMutation       = useMarkPaymentReceived();
  const payWorkshopMutation = useMarkWorkshopPaid();
  const custPayMutation        = useMarkCustomerPaid();         
  const settleMutation        = useSettleClaim();
  const closeMutation         = useCloseCase();

  const apiClaims = data?.claims || null;
  console.log("Fetched claims:", apiClaims);
  const handledRef = useRef(false);


useEffect(() => {
  const { highlightCase, autoOpenModal } = location.state || {};

  // Nothing to handle
  if (!highlightCase) return;

  // Already handled this navigation — don't re-run on refetch
  if (handledRef.current) return;

  const allClaims = apiClaims || [];
  if (!allClaims.length) return;

  const claim = allClaims.find(c => c.caseNo === highlightCase);
  if (!claim) return;

  // Mark as handled BEFORE doing anything so re-renders don't re-trigger
  handledRef.current = true;

  // Only switch tab if autoOpenModal is present
  if (autoOpenModal) {
    setActiveTab(claim.claimStatus !== "ALL" ? claim.claimStatus : "ALL");
  }

  // Open modal only if autoOpenModal is specified
  switch (autoOpenModal) {
    case "claimNo":
      setClaimNoModal(claim);
      setClaimNoForm({ claimNumber: claim.claimNumber || "" });
      break;
    case "notify":
      setNotifyModal(claim);
      setNotifyForm({ channel: "EMAIL", customNote: "" });
      break;
    case "payRecv":
      setPayRecvModal(claim);
      setPayRecvForm({ receivedAmount: "", note: "", workshopPaid: false });
      break;
    case "payWorkshop":
      setPayWorkshopModal(claim);
      setPayWorkshopForm({ amount: "", note: "" });
      break;
    case "custPay":
      setCustPayModal(claim);
      setCustPayForm({ amount: String(claim.customerDue || ""), mode: "CASH", note: "" });
      break;
    case "settle":
      setSettleModal(claim);
      setSettleForm({
        settlementAmount: String(claim.paidAmount || claim.insApprovedAmount || ""),
        settlementDate: "",
        remarks: "",
      });
      break;
    case "closeCase":
      setCloseModal(claim);
      setCloseForm({ closingRemarks: "" });
      break;
    case "status":
      openStatusModal(claim);
      break;
    default:
      // highlightCase only — just highlight the row, no modal
      setActiveTab("ALL");
      break;
  }
  // Clear router state so refresh doesn't re-trigger
  // Use navigate to replace state cleanly instead of replaceState
  navigate(location.pathname, { replace: true, state: {} });
}, [apiClaims]);

  // ── Filtered claims ───────────────────────────────────────────────────────
  const sourceClaims = apiClaims  || [];
  const claims = useMemo(() => {
    let c = [...sourceClaims];
    if (!apiClaims) {
      if (activeTab !== "ALL") c = c.filter(x => x.claimStatus === activeTab);
      if (insFilter)  c = c.filter(x => x.insuranceCompany?.toLowerCase().includes(insFilter.toLowerCase()));
      if (debouncedSearch) {
        const q = debouncedSearch.toLowerCase();
        c = c.filter(x =>
          x.caseNo?.toLowerCase().includes(q) ||
          (x.customerName||"").toLowerCase().includes(q) ||
          (x.vehicleNo||"").toLowerCase().includes(q) ||
          (x.claimNumber||"").toLowerCase().includes(q)
        );
      }
    }
    return c;
  }, [activeTab, insFilter, search, sourceClaims, apiClaims]);

  const allClaims = data?.claims || [];
  const metrics = useMemo(() => ({
  total:         allClaims.length,
  noClaimNo:     allClaims.filter(c => !c.claimNumber).length,
  approved:      allClaims.filter(c => ["APPROVED","PAYMENT_REQUESTED","PAYMENT_RECEIVED","SETTLED","CASE_CLOSED"].includes(c.claimStatus)).length,
  rejected:      allClaims.filter(c => c.claimStatus === "REJECTED").length,
  pendingNotify: allClaims.filter(c => c.customerDue > 0 && !c.customerNotified && !["NO_CLAIM","REJECTED","CASE_CLOSED"].includes(c.claimStatus)).length,
  received:      allClaims.filter(c => ["PAYMENT_RECEIVED","SETTLED","CASE_CLOSED"].includes(c.claimStatus)).length,
    }), [allClaims]);
console.log("amountForm", amountForm);

  // ── Handlers ──────────────────────────────────────────────────────────────

 // ─── Save handler body — paste this into ClaimsPage as handleSaveAmount ───────

  const handleSaveAmount = async (amountForm) => {
  console.log("Saving amounts with form data:", amountForm);
   const num = (v) => parseFloat(v) || 0;

   // ── Step 1: Invoice ──────────────────────────────────────────────────────
  const taxable      = num(amountForm.taxableAmount);
  const gstPercent   = parseFloat(amountForm.gstPercent) || 18;
  const autoGst      = Math.round(taxable * (gstPercent / 100));
  const gst          = amountForm.gstAmount    !== "" ? num(amountForm.gstAmount)    : autoGst;
  const towing       = num(amountForm.towingAmount);
  const autoInvoice  = taxable + gst + towing;
  const totalInvoice = amountForm.totalInvoice !== "" ? num(amountForm.totalInvoice) : autoInvoice;

  // ── Step 2: Deductions ───────────────────────────────────────────────────
  const dep          = num(amountForm.depreciation);
  const imt23        = num(amountForm.imt23);
  const consumable   = num(amountForm.consumable);
  const salvage      = num(amountForm.salvage);
  const fileCharge   = num(amountForm.fileCharge);
  const autoCost     = dep + imt23 + consumable + salvage + fileCharge;
  const costToCustomer = amountForm.costToCustomer !== ""
    ? num(amountForm.costToCustomer)
    : autoCost;                                   // FIX: was reading undeclared local var

  // ── Step 3: Discount & partial ───────────────────────────────────────────
  const assessed       = taxable - costToCustomer; // now always a valid number
  const workshopDiscount = num(amountForm.workshopDiscount);
  const autoNet        = Math.max(0, totalInvoice - workshopDiscount);
  const netAfterDiscount = amountForm.netAfterDiscount !== ""
    ? num(amountForm.netAfterDiscount)
    : autoNet;

  const partialApproval  = amountForm.partialApproval;
  const insApproved      = partialApproval ? num(amountForm.insApprovedAmount) : null;
  const autoShortfall    = partialApproval
    ? Math.max(0, netAfterDiscount - (insApproved || 0))
    : 0;
  const shortfall        = partialApproval        // FIX: was amountForm.customerShortfallAmount
    ? (amountForm.shortfallAmount !== "" ? num(amountForm.shortfallAmount) : autoShortfall)
    : 0;

  // ── Derived finals ───────────────────────────────────────────────────────
  const approvedAmount = partialApproval          // FIX: assessed is now valid, not NaN
    ? (amountForm.insApprovedAmount || 0)
    : assessed;
  const customerDue    = Math.max(
    0,
    totalInvoice - approvedAmount - workshopDiscount + shortfall,
  );
  console.log("Calculated amounts:", {totalInvoice,autoInvoice,shortfall,approvedAmount, customerDue, partialApproval,assessed,insApproved, autoShortfall });

   const payload = {
    caseId:            amountModal.caseNo,
    taxableAmount:     taxable,
    gstRate:           gstPercent,   // FIX 3: send as gstRate (backend field name)
    gstAmount:         gst,
    towingAmount:      towing,
    totalInvoice,
    depreciation:      dep,
    imt23,
    consumable,
    salvage,
    fileCharge,
    costToCustomer,    // now correctly from form
    assessedAmount:    assessed,
    workshopDiscount,
    netAfterDiscount,
    partialApproval,
    insApprovedAmount: insApproved,
    customerShortfall: shortfall || 0,    // backend expects this name (mutation handles it)
    approvedAmount,
    customerDue,
    note:              amountForm.note || null,
  };

   try {
     await updateAmountsMutation.mutateAsync(payload);
   } catch(err) {
    console.error("Failed to save amounts:", err);
   }
   setAmountModal(null);
   setAmountForm(AMOUNT_FORM_DEFAULT);
  };

  const handleSaveClaimNo = async () => {
   if (!claimNoForm.claimNumber?.trim()) return;
   const claimNumber = claimNoForm.claimNumber.trim();

   try {
     await updateClaimNoMutation.mutateAsync({
       caseId: claimNoModal.caseNo,
       claimNumber,
     });
   } catch(err) {
       console.error("Failed to save claim number:", err);
   }

   setClaimNoModal(null);
   setClaimNoForm({ claimNumber: "" });
  };

  const handleStatusUpdate = async () => {
    if (!statusForm.status) return;
    try {
      await updateStatusMutation.mutateAsync({
        caseId:          statusModal.caseNo,
        status:          statusForm.status,
        note:            statusForm.note || null,
        rejectionReason: statusForm.rejectionReason || null,
        approvedAt:      statusForm.approvedAt || null,
      });
      setStatusModal(null);
    } catch(err) {
      console.error("Failed to update status:", err);
    }
  };

  const handleNotifyCustomer = async () => {
    try {
      await notifyMutation.mutateAsync({
        caseId:     notifyModal.caseNo,
        channel:    notifyForm.channel,
        customNote: notifyForm.customNote || null,
      });
    } catch(err) {
      console.error("Failed to send notification", err);
    }
    setNotifyModal(null);
    setNotifyForm({ channel:"EMAIL", customNote:"" });
  };

  const handlePaymentReceived = async () => {
    const received = Number(payRecvForm.receivedAmount) || payRecvModal.insApprovedAmount;
    try {
      await payRecvMutation.mutateAsync({
        caseId:         payRecvModal.caseNo,
        receivedAmount: received,
        workshopPaid:   payRecvForm.workshopPaid,
        note:           payRecvForm.note || null,
      });
    } catch(err) {
       console.error("Failed to mark payment received:", err);
    }
    setPayRecvModal(null);
    setPayRecvForm({ receivedAmount:"", note:"", workshopPaid:false });
  };

  const handlePayWorkshop = async () => {
    const amount = Number(payWorkshopForm.amount) || payWorkshopModal.totalInvoice;
    try {
      await payWorkshopMutation.mutateAsync({
        caseId:  payWorkshopModal.caseNo,
        amount,
        note:    payWorkshopForm.note || null,
      });
    } catch(err) {
      console.error("Failed to mark workshop payment:", err);
    }
  setPayWorkshopModal(null);
  setPayWorkshopForm({ amount: "", note: "" });
  };

  const handleSettleClaim = async () => {
    const amount = Number(settleForm.settlementAmount) || settleModal.paidAmount || settleModal.insApprovedAmount;
    try {
      await settleMutation.mutateAsync({
        caseId:           settleModal.caseNo,
        settlementAmount: amount,
        settlementDate:   settleForm.settlementDate || null,
        remarks:          settleForm.remarks        || null,
      });
    } catch(err) {
       console.error("Failed to settle claim:", err);
    }
    setSettleModal(null);
    setSettleForm({ settlementAmount:"", settlementDate:"", remarks:"" });
  };

  const handleCustomerPayment = async () => {
    const amount = Number(custPayForm.amount) || custPayModal.customerDue || 0;
    try {
      await custPayMutation.mutateAsync({
        caseId: custPayModal.caseNo,
        amount,
        mode:   custPayForm.mode,
        note:   custPayForm.note || null,
      });
    } catch(err) {
      console.error("Failed to mark customer payment",err);
    }
    setCustPayModal(null);
    setCustPayForm({ amount:"", mode:"CASH", note:"" });
  };

  const handleCloseCase = async () => {
    try {
      await closeMutation.mutateAsync({
        caseId:         closeModal.caseNo,
        closingRemarks: closeForm.closingRemarks || null,
      });
    } catch(err) {
      console.error("Failed to close case:", err);
    }
    setCloseModal(null);
    setCloseForm({ closingRemarks:"" });
  };

  const openStatusModal = (claim) => {
    setStatusModal(claim);
    setStatusForm({ status: claim.claimStatus, note:"", approvedAt: claim.claimApprovedAt ? claim.claimApprovedAt.slice(0,16) : "", rejectionReason:"" });
  };

  return (
    <div className="wd-animate" style={{ display:"flex", flexDirection:"column", gap:16, minWidth:0, width:"100%", boxSizing:"border-box" }}>

      <PageHeader
        title="Claims & Payments"
        subtitle="Track insurance claims — enter claim numbers from insurance email and update status from portal"
        actions={
          <input className="wd-input" style={{ width:180 }} placeholder="Filter by insurer..."
            value={insFilter} onChange={e => setInsFilter(e.target.value)} />
        }
      />

      {/* Metrics */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(6,minmax(0,1fr))", gap:10 }}>
        <MetricCard label="Total Claims"       value={metrics.total}          color="#3b82f6" />
        <MetricCard label="No Claim No. Yet"   value={metrics.noClaimNo}      color="#eab308" />
        <MetricCard label="Approved"           value={metrics.approved}       color="#22c55e" />
        <MetricCard label="Rejected"           value={metrics.rejected}       color="#ef4444" />
        <MetricCard label="Notify Customer"    value={metrics.pendingNotify}  color="#f97316" />
        <MetricCard label="Payment Received"   value={metrics.received}       color="#14b8a6" />
      </div>

      {/* Info banner */}
      <div style={{ display:"flex", gap:10, padding:"10px 16px", background:"rgba(59,130,246,.07)", borderRadius:10, border:"1px solid rgba(59,130,246,.15)", fontSize:12, color:"var(--wd-blue)", lineHeight:1.6, overflow:"hidden" }}>
        <span style={{ flexShrink:0 }}>ℹ️</span>
        <span>
          <strong>Workflow:</strong> Insurance company sends a claim number via email → Admin enters it here → Admin checks insurance portal regularly and updates status → Once approved, admin enters approved amount + negotiated workshop discount → System calculates customer due amount → Admin sends notification email to customer.
        </span>
      </div>

      {/* Status tabs */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          {STATUS_TABS.map(t => {
              const sourceClaims = data?.claims || [];
              const count = t.key === "ALL" ? sourceClaims.length : sourceClaims.filter(c => c.claimStatus === t.key).length;
            return (
              <button key={t.key}
                className={`wd-filter-tab ${activeTab === t.key ? "active" : ""}`}
                onClick={() => setActiveTab(t.key)}
              >
                {t.label}
                <span style={{ marginLeft:5, fontSize:10, fontWeight:700, padding:"1px 5px", borderRadius:10, background:"rgba(255,255,255,.12)" }}>{count}</span>
              </button>
            );
          })}
        </div>
        <SearchInput value={search} onChange={handleSearchChange} placeholder="Case no, customer, vehicle, claim no..." />
      </div>

      {/* Claims table */}
      {isLoading ? <Spinner /> :
       isError   ? <ErrorState message="Failed to load claims" onRetry={refetch} /> :
       claims.length === 0 ? <EmptyState message="No claims found" /> : (
        <div className="wd-card" style={{ overflow:"hidden" }}>
          <div style={{ overflowX:"auto", overflowY:"visible", WebkitOverflowScrolling:"touch" }}>
            <table style={{ width:"100%", minWidth:1280, borderCollapse:"collapse", tableLayout:"fixed" }}>
              <colgroup>
                <col style={{ width:"10%" }} />
                <col style={{ width:"10%" }} />
                <col style={{ width:"10%" }} />
                <col style={{ width:"12%" }} />
                <col style={{ width:"8%"  }} />
                <col style={{ width:"9%"  }} />
                <col style={{ width:"7%"  }} />
                <col style={{ width:"9%"  }} />
                <col style={{ width:"9%"  }} />
                <col style={{ width:"7%"  }} />
                <col style={{ width:"9%"  }} />
              </colgroup>
              <thead>
                <tr style={{ background:"rgba(74,124,104,.3)", borderBottom:"2px solid rgba(74,124,104,.5)" }}>
                  {["CASE","CUSTOMER","INSURER","CLAIM NUMBER","ESTIMATE","APPROVED","DISCOUNT","CUSTOMER DUE","STATUS","CHECKED","ACTIONS"].map(h => (
                    <th key={h} style={{
                      padding:"10px 12px", textAlign:"left", fontSize:11,
                      fontWeight:700, color:"#fff", letterSpacing:".05em",
                      textTransform:"uppercase", whiteSpace:"nowrap",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {claims.map(c => {
                  const cfg = STATUS_CFG[c.claimStatus] || STATUS_CFG.NO_CLAIM;
                  const isHighlighted = location.state?.highlightCase === c.caseNo;

                  return (
                    <tr key={c.id}
                      style={{ borderBottom:"1px solid var(--wd-border)", cursor:"pointer", background:isHighlighted ? "rgba(59,130,246,.07)" : "transparent", transition:"background .12s" }}
                      onClick={() => setDetailClaim(c)}
                      onMouseEnter={e => e.currentTarget.style.background = "var(--wd-hover)"}
                      onMouseLeave={e =>  e.currentTarget.style.background = isHighlighted ? "rgba(59,130,246,.07)" : "transparent"}
                    >
                      {/* CASE */}
                      <td style={{ padding:"12px 12px" }}>
                        <div style={{ fontFamily:"var(--wd-mono)", fontWeight:700, color:"var(--wd-blue)", fontSize:12 }}>{c.caseNo}</div>
                        <div style={{ fontSize:10, color:"var(--wd-t3)", fontFamily:"var(--wd-mono)", marginTop:1 }}>{c.vehicleNo}</div>
                      </td>
                      {/* CUSTOMER */}
                      <td style={{ padding:"12px 12px", overflow:"hidden" }}>
                        <div style={{ fontWeight:600, fontSize:13, color:"var(--wd-t1)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{c.customerName}</div>
                      </td>
                      {/* INSURER */}
                      <td style={{ padding:"12px 12px", fontSize:12, color:"var(--wd-t2)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {c.insuranceCompany}
                      </td>
                      {/* CLAIM NUMBER */}
                      <td style={{ padding:"12px 12px" }}>
                        {c.claimNumber
                          ? <span style={{ fontFamily:"var(--wd-mono)", fontSize:11, color:"var(--wd-t1)", fontWeight:600, wordBreak:"break-all" }}>{c.claimNumber}</span>
                          : <button className="wd-btn wd-btn-ghost wd-btn-sm"
                              style={{ color:"var(--wd-orange)", borderColor:"rgba(249,115,22,.3)", fontSize:11 }}
                              onClick={e => { e.stopPropagation(); setClaimNoModal(c); setClaimNoForm({ claimNumber:"" }); }}>
                              + Add Claim No.
                            </button>
                        }
                      </td>
                      {/* ESTIMATE */}
                      <td style={{ padding:"12px 12px", fontFamily:"var(--wd-mono)", fontSize:12, color:"var(--wd-t2)" }}>
                        {c.estimateAmount ? formatINR(c.estimateAmount) : "—"}
                      </td>
                      {/* APPROVED */}
                      <td style={{ padding:"12px 12px" }}>
                        <div style={{ fontFamily:"var(--wd-mono)", fontSize:12, color: c.insApprovedAmount ? "var(--wd-green)" : "var(--wd-t3)", fontWeight: c.insApprovedAmount ? 600 : 400 }}>
                          {c.insApprovedAmount ? formatINR(c.insApprovedAmount) : "—"}
                        </div>
                        {c.claimApprovedAt && (
                          <div style={{ fontSize:10, color:"var(--wd-t3)", marginTop:1 }}>{formatDate(c.claimApprovedAt)}</div>
                        )}
                      </td>
                      {/* DISCOUNT */}
                      <td style={{ padding:"12px 12px", fontFamily:"var(--wd-mono)", fontSize:12, color:"var(--wd-teal)" }}>
                        {c.workshopDiscount ? formatINR(c.workshopDiscount) : "—"}
                      </td>
                      {/* CUSTOMER DUE */}
                      <td style={{ padding:"12px 12px" }}>
                        {c.customerDue != null
                          ? <span style={{ fontFamily:"var(--wd-mono)", fontSize:12, fontWeight:700,
                              color: c.customerDue === 0 ? "var(--wd-green)" : "var(--wd-orange)" }}>
                              {c.customerDue === 0 ? "NIL" : formatINR(c.customerDue)}
                            </span>
                          : <span style={{ color:"var(--wd-t3)", fontSize:12 }}>—</span>
                        }
                        {c.claimStatus === "APPROVED" && c.customerDue > 0 && !c.customerNotified && (
                          <div style={{ fontSize:10, color:"var(--wd-orange)", marginTop:2 }}>⚠ Not notified</div>
                        )}
                        {c.customerNotified && (
                          <div style={{ fontSize:10, color:"var(--wd-green)", marginTop:2 }}>✓ Notified</div>
                        )}
                      </td>
                      {/* STATUS */}
                      <td style={{ padding:"12px 12px" }}>
                        <Badge variant={cfg.variant}>{cfg.label}</Badge>
                        {/* ADD THIS */}
                        {["PAYMENT_RECEIVED","SETTLED"].includes(c.claimStatus) && c.workshopPaymentStatus !== "PAID" && (
                          <div style={{ fontSize:10, color:"var(--wd-yellow)", marginTop:3 }}>⚠ Workshop unpaid</div>
                          )}
                        {["PAYMENT_RECEIVED","SETTLED","CASE_CLOSED"].includes(c.claimStatus) && c.workshopPaymentStatus === "PAID" && (
                          <div style={{ fontSize:10, color:"var(--wd-green)", marginTop:3 }}>✓ Workshop paid</div>
                        )}
                        {c.customerPaymentStatus === "PAID" && customerPaymentDone(c) && !["NO_CLAIM","REJECTED"].includes(c.claimStatus) && (
                          <div style={{ fontSize:10, color:"var(--wd-green)", marginTop:3 }}>✓ Customer payment collected</div>
                        )}
                        {c.customerPaymentStatus === "PENDING" && !customerPaymentDone(c) && c.customerNotified && !["NO_CLAIM","REJECTED","CASE_CLOSED"].includes(c.claimStatus) && (
                          <div style={{ fontSize:10, color:"var(--wd-orange)", marginTop:3 }}>⚠ Customer payment pending</div>
                        )}
                      </td>
                      {/* LAST CHECKED */}
                      <td style={{ padding:"12px 12px", fontSize:11, color:"var(--wd-t3)", whiteSpace:"nowrap" }}>
                        {c.lastStatusCheckedAt ? formatDate(c.lastStatusCheckedAt) : "—"}
                      </td>
                      {/* ACTIONS */}
                      <td style={{ padding:"12px 12px" }} onClick={e => e.stopPropagation()}>
                        <ActionButtons
                          claim={c}
                          onClaimNo={() => { setClaimNoModal(c); setClaimNoForm({ claimNumber: c.claimNumber || "" }); }}
                          onStatus={() => openStatusModal(c)}
                          onAmounts={() => openAmountModal(c, setAmountModal, setAmountForm)}
                          onNotify={() => { setNotifyModal(c); setNotifyForm({ channel:"EMAIL", customNote:"" }); }}
                          onReject={() => setRejectModal(c)}
                          onPayRecv={() => { setPayRecvModal(c); setPayRecvForm({ receivedAmount:"", note:"", workshopPaid:false }); }}
                          onPayWorkshop={() => {setPayWorkshopModal(c);setPayWorkshopForm({ amount: "", note: "" });}}
                          onCustPay={    () => { setCustPayModal(c);     setCustPayForm({ amount:String(c.customerDue || ""), mode:"CASH", note:"" }); }}
                          onSettle={() => { setSettleModal(c); setSettleForm({ settlementAmount: String(c.paidAmount||c.insApprovedAmount||""), settlementDate:"", remarks:"" }); }}
                          onClose={() => { setCloseModal(c); setCloseForm({ closingRemarks:"" }); }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
       )
      }

      {/* ── Modals ── */}

      {/* 1. Add / Edit Claim Number */}
      <Modal open={!!claimNoModal} onClose={() => setClaimNoModal(null)}
        title={claimNoModal?.claimNumber ? "Edit Claim Number" : "Add Claim Number"}
        footer={<>
          <button className="wd-btn wd-btn-ghost" onClick={() => setClaimNoModal(null)}>Cancel</button>
          <button className="wd-btn wd-btn-primary" onClick={handleSaveClaimNo}
            disabled={!claimNoForm.claimNumber.trim() || updateClaimNoMutation.isPending}>
            {updateClaimNoMutation.isPending ? "Saving..." : "Save"}
          </button>
        </>}
      >
        <div style={{ display:"flex", gap:10, padding:"10px 14px", background:"rgba(59,130,246,.07)", borderRadius:10, border:"1px solid rgba(59,130,246,.15)", fontSize:12, color:"var(--wd-blue)" }}>
          <span>ℹ️</span>
          <span>Enter the claim number exactly as received in the email from <strong>{claimNoModal?.insuranceCompany}</strong>. This is used to track the claim on their portal.</span>
        </div>
        {claimNoModal && (
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <div style={{ display:"flex", gap:12, padding:"10px 14px", background:"var(--wd-hover)", borderRadius:8 }}>
              <div><span style={{ fontSize:11, color:"var(--wd-t3)" }}>Case</span><div style={{ fontFamily:"var(--wd-mono)", fontWeight:700, color:"var(--wd-blue)" }}>{claimNoModal.caseNo}</div></div>
              <div><span style={{ fontSize:11, color:"var(--wd-t3)" }}>Insurer</span><div style={{ fontSize:13, fontWeight:600 }}>{claimNoModal.insuranceCompany}</div></div>
              <div><span style={{ fontSize:11, color:"var(--wd-t3)" }}>Policy No</span><div style={{ fontFamily:"var(--wd-mono)", fontSize:12 }}>{claimNoModal.policyNo}</div></div>
            </div>
            <label className="wd-form-label">Claim Number (from insurance email) *</label>
            <input className="wd-input wd-mono" placeholder="e.g. CLM-HDFC-2024-45521"
              value={claimNoForm.claimNumber}
              onChange={e => setClaimNoForm({ claimNumber: e.target.value.toUpperCase() })}
              autoFocus />
          </div>
        )}
      </Modal>

      {/* 2. Update Claim Status */}
      <Modal open={!!statusModal} onClose={() => setStatusModal(null)}
        title="Update Claim Status"
        footer={<>
          <button className="wd-btn wd-btn-ghost" onClick={() => setStatusModal(null)}>Cancel</button>
          <button className="wd-btn wd-btn-primary" onClick={handleStatusUpdate}
            disabled={!statusForm.status || updateStatusMutation.isPending}>
            {updateStatusMutation.isPending ? "Updating..." : "Update Status"}
          </button>
        </>}
      >
        {statusModal && (
          <>
            <div style={{ display:"flex", gap:10, padding:"10px 14px", background:"rgba(59,130,246,.07)", borderRadius:10, border:"1px solid rgba(59,130,246,.15)", fontSize:12, color:"var(--wd-blue)" }}>
              <span>ℹ️</span>
              <span>Update this after checking the status on <strong>{statusModal.insuranceCompany}</strong>'s claims portal or receiving an update via email.</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", background:"var(--wd-hover)", borderRadius:10 }}>
              <span style={{ fontSize:12, color:"var(--wd-t3)" }}>Current:</span>
              <Badge variant={(STATUS_CFG[statusModal.claimStatus]||{}).variant||"gray"}>
                {STATUS_CFG[statusModal.claimStatus]?.label || statusModal.claimStatus}
              </Badge>
              {statusModal.claimNumber && (
                <span style={{ fontFamily:"var(--wd-mono)", fontSize:11, color:"var(--wd-t3)", marginLeft:"auto" }}>
                  {statusModal.claimNumber}
                </span>
              )}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label className="wd-form-label">New Status *</label>
              <select className="wd-input wd-select" value={statusForm.status}
                onChange={e => setStatusForm(f => ({ ...f, status: e.target.value }))}>
                <option value="">Select status...</option>
                <option value="CLAIM_NUMBER_ADDED">Claim Number Added</option>
                <option value="UNDER_REVIEW">Under Review — insurance is reviewing</option>
                <option value="APPROVED">Approved — insurance approved the claim</option>
                <option value="REJECTED">Rejected — insurance rejected the claim</option>
                <option value="PAYMENT_REQUESTED">Payment Requested — sent payment request</option>
                <option value="PAYMENT_RECEIVED">Payment Received — payment confirmed</option>
              </select>
            </div>
            <div style={{ fontSize:11, color:"var(--wd-t3)", padding:"6px 10px", background:"var(--wd-hover)", borderRadius:8 }}>
              💡 To settle or close a case, use the <strong style={{ color:"var(--wd-t2)" }}>Settle</strong> or <strong style={{ color:"var(--wd-t2)" }}>Close Case</strong> buttons in the actions column.
            </div>
            {statusForm.status === "APPROVED" && (
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <label className="wd-form-label">Date & Time Approved by Insurance *</label>
                <input className="wd-input" type="datetime-local"
                  value={statusForm.approvedAt}
                  max={new Date().toISOString().slice(0,16)}
                  onChange={e => setStatusForm(f => ({ ...f, approvedAt: e.target.value }))} />
                <span style={{ fontSize:11, color:"var(--wd-t3)" }}>
                  Enter the exact date and time when the insurance company approved the claim (from their email or portal).
                </span>
              </div>
            )}
            {statusForm.status === "REJECTED" && (
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <label className="wd-form-label">Rejection Reason *</label>
                <textarea className="wd-input" rows={3} style={{ resize:"vertical" }}
                  placeholder="Enter reason as stated by insurance company..."
                  value={statusForm.rejectionReason || ""}
                  onChange={e => setStatusForm(f => ({ ...f, rejectionReason: e.target.value }))} />
              </div>
            )}
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label className="wd-form-label">Note (optional)</label>
              <textarea className="wd-input" rows={2} style={{ resize:"vertical" }}
                placeholder="e.g. Checked on HDFC portal at 10 AM — status changed to Approved"
                value={statusForm.note}
                onChange={e => setStatusForm(f => ({ ...f, note: e.target.value }))} />
            </div>
          </>
        )}
      </Modal>

      {/* 3. Enter Approved Amount & Cost Breakdown — NEW AmountModal */}
      <AmountModal
        claim={amountModal}
        form={amountForm}
        setForm={setAmountForm}
        onClose={() => { setAmountModal(null); setAmountForm(AMOUNT_FORM_DEFAULT); }}
        onSave={handleSaveAmount}
        isPending={updateAmountsMutation.isPending}
      />

      {/* 4. Send Customer Notification */}
      <Modal open={!!notifyModal} onClose={() => setNotifyModal(null)}
        title="Notify Customer — Remaining Amount"
        footer={<>
          <button className="wd-btn wd-btn-ghost" onClick={() => setNotifyModal(null)}>Cancel</button>
          <button className="wd-btn wd-btn-primary" onClick={handleNotifyCustomer}>
            {notifyMutation.isPending ? "Sending..." : "📧 Send Email to Customer"}
          </button>
        </>}
      >
        {notifyModal && (
          <>
            <div style={{ display:"flex", gap:14, padding:"14px 16px", background:"var(--wd-hover)", borderRadius:10 }}>
              <Avatar name={notifyModal.customerName} size={42} />
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:14 }}>{notifyModal.customerName}</div>
                <div style={{ fontSize:12, color:"var(--wd-t3)" }}>{notifyModal.customerEmail}</div>
                <div style={{ fontSize:12, color:"var(--wd-t3)" }}>{notifyModal.vehicleNo}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:11, color:"var(--wd-t3)" }}>Remaining Amount</div>
                <div style={{ fontFamily:"var(--wd-mono)", fontSize:22, fontWeight:800,
                  color: notifyModal.customerDue === 0 ? "var(--wd-green)" : "var(--wd-orange)" }}>
                  {notifyModal.customerDue === 0 ? "NIL" : formatINR(notifyModal.customerDue)}
                </div>
              </div>
            </div>
            <div style={{ background:"var(--wd-surface)", borderRadius:10, overflow:"hidden", border:"1px solid var(--wd-border)" }}>
              <div style={{ background:"var(--wd-hover)", padding:"8px 14px", fontSize:11, color:"var(--wd-t3)", borderBottom:"1px solid var(--wd-border)" }}>
                📧 Email preview — will be sent to <strong style={{ color:"var(--wd-t2)" }}>{notifyModal.customerEmail}</strong>
              </div>
              <div style={{ padding:16, fontSize:12, color:"var(--wd-t2)", lineHeight:1.8 }}>
                <div style={{ fontWeight:700, color:"var(--wd-t1)", marginBottom:8 }}>Claim Settlement Update — {notifyModal.caseNo}</div>
                <div>Dear {notifyModal.customerName},</div>
                <div style={{ marginTop:6 }}>Your insurance claim has been processed by <strong>{notifyModal.insuranceCompany}</strong>.</div>
                <div style={{ margin:"10px 0", padding:"10px 14px", background:"var(--wd-hover)", borderRadius:8 }}>
                  <div>Workshop Estimate: <strong style={{ fontFamily:"var(--wd-mono)" }}>{formatINR(notifyModal.estimateAmount)}</strong></div>
                  <div>Insurance Approved: <strong style={{ fontFamily:"var(--wd-mono)", color:"var(--wd-green)" }}>{formatINR(notifyModal.insApprovedAmount)}</strong></div>
                  <div>Deductions : <strong style={{ fontFamily:"var(--wd-mono)", color:"var(--wd-green)" }}>{formatINR(notifyModal.costToCustomer)}</strong></div>
                  <div>GST Amount: <strong style={{ fontFamily:"var(--wd-mono)", color:"var(--wd-green)" }}>{formatINR(notifyModal.gstAmount)}</strong></div>


                  {notifyModal.workshopDiscount > 0 && (
                    <div>Workshop Discount: <strong style={{ fontFamily:"var(--wd-mono)", color:"var(--wd-teal)" }}>− {formatINR(notifyModal.workshopDiscount)}</strong></div>
                  )}
                  <div style={{ borderTop:"1px solid var(--wd-border)", marginTop:6, paddingTop:6 }}>
                    <strong>Your remaining amount: </strong>
                    <strong style={{ fontFamily:"var(--wd-mono)", fontSize:15,
                      color: notifyModal.customerDue === 0 ? "var(--wd-green)" : "var(--wd-orange)" }}>
                      {notifyModal.customerDue === 0 ? "NIL — Fully covered by insurance" : formatINR(notifyModal.customerDue)}
                    </strong>
                  </div>
                </div>
                {notifyModal.customerDue > 0 && (
                  <div>Please arrange payment of <strong>{formatINR(notifyModal.customerDue)}</strong> at the workshop before vehicle delivery.</div>
                )}
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label className="wd-form-label">Channel</label>
              <div style={{ display:"flex", gap:8 }}>
                {["EMAIL","SMS","BOTH"].map(ch => (
                  <button key={ch} className="wd-btn wd-btn-ghost wd-btn-sm"
                    style={notifyForm.channel === ch ? { borderColor:"var(--wd-blue)", color:"var(--wd-blue)", background:"var(--wd-blue-soft)" } : {}}
                    onClick={() => setNotifyForm(f => ({ ...f, channel: ch }))}>
                    {ch}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label className="wd-form-label">Additional Note (optional)</label>
              <textarea className="wd-input" rows={2} style={{ resize:"vertical" }}
                placeholder="Any additional message for the customer..."
                value={notifyForm.customNote}
                onChange={e => setNotifyForm(f => ({ ...f, customNote: e.target.value }))} />
            </div>
          </>
        )}
      </Modal>

      {/* 5. Mark Payment Received */}
      <Modal open={!!payRecvModal} onClose={() => setPayRecvModal(null)}
        title="Mark Payment Received"
        footer={<>
          <button className="wd-btn wd-btn-ghost" onClick={() => setPayRecvModal(null)}>Cancel</button>
          <button className="wd-btn wd-btn-primary" onClick={handlePaymentReceived}>
            {payRecvMutation.isPending ? "Saving..." : "✓ Confirm Payment Received"}
          </button>
        </>}
      >
        {payRecvModal && (
          <>
            <div style={{ display:"flex", gap:14, padding:"12px 16px", background:"var(--wd-hover)", borderRadius:10 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:"var(--wd-mono)", fontWeight:700, color:"var(--wd-blue)" }}>{payRecvModal.caseNo}</div>
                <div style={{ fontSize:13, fontWeight:600, marginTop:2 }}>{payRecvModal.customerName}</div>
                <div style={{ fontSize:12, color:"var(--wd-t3)", marginTop:1 }}>{payRecvModal.insuranceCompany} · {payRecvModal.claimNumber}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:11, color:"var(--wd-t3)" }}>Approved Amount</div>
                <div style={{ fontFamily:"var(--wd-mono)", fontSize:18, fontWeight:800, color:"var(--wd-green)" }}>
                  {formatINR(payRecvModal.insApprovedAmount)}
                </div>
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label className="wd-form-label">Amount Actually Received (₹) *</label>
              <input className="wd-input wd-mono" type="number"
                placeholder={`e.g. ${payRecvModal.insApprovedAmount || 0}`}
                value={payRecvForm.receivedAmount}
                onChange={e => setPayRecvForm(f => ({ ...f, receivedAmount: e.target.value }))} />
              <span style={{ fontSize:11, color:"var(--wd-t3)" }}>
                Leave blank to use approved amount ({formatINR(payRecvModal.insApprovedAmount)})
              </span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 14px", background:"var(--wd-hover)", borderRadius:10, cursor:"pointer" }}
              onClick={() => setPayRecvForm(f => ({ ...f, workshopPaid: !f.workshopPaid }))}>
              <div style={{
                width:18, height:18, borderRadius:4, border:"2px solid var(--wd-border-md)",
                background: payRecvForm.workshopPaid ? "var(--wd-green)" : "transparent",
                display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
              }}>
                {payRecvForm.workshopPaid && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
              </div>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:"var(--wd-t1)" }}>Mark workshop payment as PAID</div>
                <div style={{ fontSize:11, color:"var(--wd-t3)" }}>Check this if you have also paid {payRecvModal.workshopName}</div>
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label className="wd-form-label">Note (optional)</label>
              <textarea className="wd-input" rows={2} style={{ resize:"vertical" }}
                placeholder="e.g. NEFT received in bank account, UTR: XXXXX"
                value={payRecvForm.note}
                onChange={e => setPayRecvForm(f => ({ ...f, note: e.target.value }))} />
            </div>
            <div style={{ padding:"10px 14px", background:"rgba(34,197,94,.07)", borderRadius:10, border:"1px solid rgba(34,197,94,.2)", fontSize:12, color:"var(--wd-green)" }}>
              ✓ This will update the claim status to <strong>PAYMENT RECEIVED</strong> and record the amount.
            </div>
          </>
        )}
      </Modal>

      {/* 5b. Pay Workshop */}
      <Modal open={!!payWorkshopModal} onClose={() => setPayWorkshopModal(null)}
        title="Mark Workshop Payment"
        footer={<>
            <button className="wd-btn wd-btn-ghost" onClick={() => setPayWorkshopModal(null)}>Cancel</button>
            <button className="wd-btn wd-btn-primary" onClick={handlePayWorkshop}
            disabled={payWorkshopMutation?.isPending}
            style={{ background:"var(--wd-yellow)", borderColor:"var(--wd-yellow)", color:"#000" }}>
            {payWorkshopMutation?.isPending ? "Saving..." : "✓ Confirm Workshop Paid"}
            </button>
          </>}
      >
          {payWorkshopModal && (
          <>
          {/* Workshop info */}
          <div style={{ display:"flex", gap:14, padding:"12px 16px", background:"var(--wd-hover)", borderRadius:10 }}>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:"var(--wd-mono)", fontWeight:700, color:"var(--wd-blue)" }}>{payWorkshopModal.caseNo}</div>
            <div style={{ fontSize:13, fontWeight:600, marginTop:2 }}>{payWorkshopModal.workshopName}</div>
          <div style={{ fontSize:12, color:"var(--wd-t3)", marginTop:1 }}>{payWorkshopModal.customerName} · {payWorkshopModal.vehicleNo}</div>
        </div>
          <div style={{ textAlign:"right" }}>
            <div style={{ fontSize:11, color:"var(--wd-t3)" }}>Total Workshop Invoice</div>
            <div style={{ fontFamily:"var(--wd-mono)", fontSize:18, fontWeight:800, color:"var(--wd-green)" }}>
              {formatINR(payWorkshopModal.totalInvoice)}
            </div>
          </div>
        </div>

        {/* Warning if not yet settled */}
        {payWorkshopModal.claimStatus !== "SETTLED" && (
          <div style={{ display:"flex", gap:10, padding:"10px 14px", background:"rgba(234,179,8,.08)", borderRadius:10, border:"1px solid rgba(234,179,8,.25)", fontSize:12, color:"var(--wd-yellow)" }}>
            <span>⚠️</span>
            <span>Claim is not yet settled. You can still record the workshop payment, but make sure to settle the claim afterwards.</span>
          </div>
        )}

      {/* Amount */}
      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
        <label className="wd-form-label">Amount Paid to Workshop (₹) *</label>
        <input className="wd-input wd-mono" type="number"
          placeholder={String(payWorkshopModal.totalInvoice || "")}
          value={payWorkshopForm.amount}
          onChange={e => setPayWorkshopForm(f => ({ ...f, amount: e.target.value }))}
          autoFocus />
        <span style={{ fontSize:11, color:"var(--wd-t3)" }}>
          Leave blank to use invoice amount ({formatINR(payWorkshopModal.totalInvoice)})
        </span>
      </div>

      {/* Note */}
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            <label className="wd-form-label">Note (optional)</label>
              <textarea className="wd-input" rows={2} style={{ resize:"vertical" }}
                placeholder="e.g. NEFT to workshop account, UTR: XXXXX"
                value={payWorkshopForm.note}
                onChange={e => setPayWorkshopForm(f => ({ ...f, note: e.target.value }))} />
          </div>

          <div style={{ padding:"10px 14px", background:"rgba(34,197,94,.07)", borderRadius:10, border:"1px solid rgba(34,197,94,.2)", fontSize:12, color:"var(--wd-green)" }}>
            ✓ This will mark <strong>{payWorkshopModal.workshopName}</strong> as paid and log the entry in the timeline.
          </div>
        </>
        )}
      </Modal>

       {/* 5c. Mark Customer Payment — NEW ─────────────────────────────────────── */}
      <Modal open={!!custPayModal} onClose={() => setCustPayModal(null)} title="Mark Customer Payment Received"
        footer={<>
          <button className="wd-btn wd-btn-ghost" onClick={() => setCustPayModal(null)}>Cancel</button>
          <button className="wd-btn wd-btn-primary" onClick={handleCustomerPayment} disabled={custPayMutation?.isPending}
            style={{ background:"var(--wd-orange)", borderColor:"var(--wd-orange)" }}>
            {custPayMutation?.isPending ? "Saving..." : "✓ Confirm Customer Paid"}
          </button>
        </>}
      >
        {custPayModal && (
          <>
            {/* Customer info header */}
            <div style={{ display:"flex", gap:14, padding:"12px 16px", background:"var(--wd-hover)", borderRadius:10 }}>
              <Avatar name={custPayModal.customerName} size={40} />
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:"var(--wd-mono)", fontWeight:700, color:"var(--wd-blue)", fontSize:12 }}>{custPayModal.caseNo}</div>
                <div style={{ fontSize:13, fontWeight:600, marginTop:2 }}>{custPayModal.customerName}</div>
                <div style={{ fontSize:12, color:"var(--wd-t3)", marginTop:1 }}>{custPayModal.vehicleNo} · {custPayModal.customerPhone}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:11, color:"var(--wd-t3)" }}>Amount Due</div>
                <div style={{ fontFamily:"var(--wd-mono)", fontSize:20, fontWeight:800, color:"var(--wd-orange)" }}>
                  {custPayModal.customerDue === 0 ? "NIL" : formatINR(custPayModal.customerDue)}
                </div>
                {custPayModal.customerNotified && (
                  <div style={{ fontSize:10, color:"var(--wd-green)", marginTop:3 }}>✓ Customer was notified</div>
                )}
              </div>
            </div>
 
            {/* NIL due — informational */}
            {custPayModal.customerDue === 0 && (
              <div style={{ display:"flex", gap:10, padding:"10px 14px", background:"rgba(34,197,94,.07)", borderRadius:10, border:"1px solid rgba(34,197,94,.2)", fontSize:12, color:"var(--wd-green)" }}>
                <span>ℹ️</span>
                <span>This customer has no outstanding amount — insurance covers the full cost. You can still log a ₹0 confirmation if needed.</span>
              </div>
            )}
 
            {/* Amount input */}
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label className="wd-form-label">Amount Collected from Customer (₹) *</label>
              <input className="wd-input wd-mono" type="number" placeholder={String(custPayModal.customerDue || "0")}
                value={custPayForm.amount} onChange={e => setCustPayForm(f => ({ ...f, amount:e.target.value }))} autoFocus />
              {custPayModal.customerDue > 0 && (
                <span style={{ fontSize:11, color:"var(--wd-t3)" }}>Due amount: {formatINR(custPayModal.customerDue)}</span>
              )}
            </div>
 
            {/* Payment mode */}
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label className="wd-form-label">Payment Mode *</label>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {["CASH","UPI","NEFT","CARD","CHEQUE"].map(mode => (
                  <button key={mode} className="wd-btn wd-btn-ghost wd-btn-sm"
                    style={custPayForm.mode === mode ? { borderColor:"var(--wd-orange)", color:"var(--wd-orange)", background:"rgba(249,115,22,.08)" } : {}}
                    onClick={() => setCustPayForm(f => ({ ...f, mode }))}>
                    {mode}
                  </button>
                ))}
              </div>
            </div>
 
            {/* Note */}
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label className="wd-form-label">Note (optional)</label>
              <textarea className="wd-input" rows={2} style={{ resize:"vertical" }}
                placeholder="e.g. Paid at workshop counter, receipt no. 1234"
                value={custPayForm.note} onChange={e => setCustPayForm(f => ({ ...f, note:e.target.value }))} />
            </div>
 
            <div style={{ padding:"10px 14px", background:"rgba(249,115,22,.07)", borderRadius:10, border:"1px solid rgba(249,115,22,.2)", fontSize:12, color:"var(--wd-orange)" }}>
              ✓ This will mark the customer payment as <strong>COLLECTED</strong> and log the entry in the timeline.
            </div>
          </>
        )}
      </Modal>
 

      {/* 6. Rejection reason view */}
      <Modal open={!!rejectModal} onClose={() => setRejectModal(null)} title="Claim Rejection Details">
        {rejectModal && (
          <>
            <div style={{ display:"flex", gap:12, padding:"12px 14px", background:"rgba(239,68,68,.07)", borderRadius:10, border:"1px solid rgba(239,68,68,.2)" }}>
              <span style={{ fontSize:20 }}>❌</span>
              <div>
                <div style={{ fontWeight:700, color:"var(--wd-red)", marginBottom:4 }}>Claim Rejected by {rejectModal.insuranceCompany}</div>
                <div style={{ fontSize:12, color:"var(--wd-t3)" }}>{rejectModal.caseNo} · {rejectModal.claimNumber}</div>
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              <span style={{ fontSize:11, fontWeight:700, color:"var(--wd-t3)", textTransform:"uppercase", letterSpacing:".05em" }}>Rejection Reason</span>
              <p style={{ fontSize:13, color:"var(--wd-t2)", lineHeight:1.7, margin:0, padding:"10px 14px", background:"var(--wd-hover)", borderRadius:8 }}>
                {rejectModal.rejectionReason || "No reason provided"}
              </p>
            </div>
          </>
        )}
      </Modal>

      {/* 7. Settle Claim Modal */}
      <Modal open={!!settleModal} onClose={() => setSettleModal(null)}
        title="Settle Claim"
        footer={<>
          <button className="wd-btn wd-btn-ghost" onClick={() => setSettleModal(null)}>Cancel</button>
          <button className="wd-btn wd-btn-primary" onClick={handleSettleClaim}
            disabled={settleMutation.isPending}>
            {settleMutation.isPending ? "Settling..." : "✓ Confirm Settlement"}
          </button>
        </>}
      >
        {settleModal && (
          <>
            <div style={{ display:"flex", gap:14, padding:"12px 16px", background:"var(--wd-hover)", borderRadius:10 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontFamily:"var(--wd-mono)", fontWeight:700, color:"var(--wd-blue)" }}>{settleModal.caseNo}</div>
                <div style={{ fontSize:13, fontWeight:600, marginTop:2 }}>{settleModal.customerName}</div>
                <div style={{ fontSize:12, color:"var(--wd-t3)" }}>{settleModal.insuranceCompany}</div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:11, color:"var(--wd-t3)" }}>Amount Received</div>
                <div style={{ fontFamily:"var(--wd-mono)", fontSize:18, fontWeight:800, color:"var(--wd-green)" }}>
                  {formatINR(settleModal.paidAmount || settleModal.insApprovedAmount)}
                </div>
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
              {[
                ["Estimate",      settleModal.estimateAmount,  "var(--wd-t2)"    ],
                ["Ins. Approved", settleModal.insApprovedAmount,  "var(--wd-green)" ],
                ["Customer Due",  settleModal.customerDue,     settleModal.customerDue === 0 ? "var(--wd-green)" : "var(--wd-orange)"],
              ].map(([label, val, color]) => (
                <div key={label} style={{ padding:"10px 12px", background:"var(--wd-surface)", borderRadius:8, textAlign:"center" }}>
                  <div style={{ fontSize:10, color:"var(--wd-t3)", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>{label}</div>
                  <div style={{ fontFamily:"var(--wd-mono)", fontWeight:700, fontSize:14, color }}>
                    {val != null ? (val === 0 ? "NIL" : formatINR(val)) : "—"}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <label className="wd-form-label">Final Settlement Amount (₹)</label>
                <input className="wd-input wd-mono" type="number"
                  placeholder={String(settleModal.paidAmount || settleModal.insApprovedAmount || "")}
                  value={settleForm.settlementAmount}
                  onChange={e => setSettleForm(f => ({ ...f, settlementAmount: e.target.value }))} />
                <span style={{ fontSize:11, color:"var(--wd-t3)" }}>Leave blank to use amount received</span>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <label className="wd-form-label">Settlement Date</label>
                <input className="wd-input" type="date"
                  value={settleForm.settlementDate}
                  max={new Date().toISOString().slice(0,10)}
                  onChange={e => setSettleForm(f => ({ ...f, settlementDate: e.target.value }))} />
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label className="wd-form-label">Settlement Remarks (optional)</label>
              <textarea className="wd-input" rows={2} style={{ resize:"vertical" }}
                placeholder="e.g. Full settlement completed. Workshop and customer payments done."
                value={settleForm.remarks}
                onChange={e => setSettleForm(f => ({ ...f, remarks: e.target.value }))} />
            </div>
            <div style={{ padding:"10px 14px", background:"rgba(34,197,94,.07)", borderRadius:10, border:"1px solid rgba(34,197,94,.2)", fontSize:12, color:"var(--wd-green)" }}>
              ✓ This will mark the claim as <strong>SETTLED</strong>. The next step is to close the case after all paperwork is done.
            </div>
          </>
        )}
      </Modal>

      {/* 8. Close Case Modal */}
      <Modal open={!!closeModal} onClose={() => setCloseModal(null)}
        title="Close Case"
        footer={<>
          <button className="wd-btn wd-btn-ghost" onClick={() => setCloseModal(null)}>Cancel</button>
          <button className="wd-btn wd-btn-primary" onClick={handleCloseCase}
            disabled={closeMutation.isPending}
            style={{ background:"var(--wd-purple)", borderColor:"var(--wd-purple)" }}>
            {closeMutation.isPending ? "Closing..." : "🔒 Close Case"}
          </button>
        </>}
      >
        {closeModal && (
          <>
            <div style={{ display:"flex", gap:10, padding:"12px 14px", background:"rgba(168,85,247,.08)", borderRadius:10, border:"1px solid rgba(168,85,247,.25)" }}>
              <span style={{ fontSize:18 }}>🔒</span>
              <div style={{ fontSize:12, color:"var(--wd-purple)", lineHeight:1.6 }}>
                <strong>This is a final action.</strong> Once the case is closed, no further updates can be made. Ensure all payments have been settled and all paperwork is complete before closing.
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              {[
                ["Case No",      closeModal.caseNo,          "var(--wd-blue)"   ],
                ["Customer",     closeModal.customerName,    "var(--wd-t1)"     ],
                ["Insurer",      closeModal.insuranceCompany,"var(--wd-t2)"     ],
                ["Paid Amount",  formatINR(closeModal.paidAmount||0), "var(--wd-green)"],
                ["Customer Due", closeModal.customerDue === 0 ? "NIL" : formatINR(closeModal.customerDue||0), closeModal.customerDue === 0 ? "var(--wd-green)" : "var(--wd-orange)"],
                ["Settled On",   closeModal.settledAt ? formatDate(closeModal.settledAt) : "—", "var(--wd-t2)"],
              ].map(([label, val, color]) => (
                <div key={label} style={{ padding:"8px 12px", background:"var(--wd-hover)", borderRadius:8 }}>
                  <div style={{ fontSize:10, color:"var(--wd-t3)", textTransform:"uppercase", fontWeight:600, marginBottom:2 }}>{label}</div>
                  <div style={{ fontSize:13, fontWeight:600, color, fontFamily: label === "Case No" || label === "Paid Amount" ? "var(--wd-mono)" : "inherit" }}>{val}</div>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <div style={{ fontSize:11, fontWeight:700, color:"var(--wd-t3)", textTransform:"uppercase", letterSpacing:".05em" }}>Pre-close checklist</div>
              {[
                ["Insurance payment received",  !!closeModal.paymentReceivedAt ],
                ["Claim settled",               !!closeModal.settledAt         ],
                ["Customer notified",           !!closeModal.customerNotified  ],
                ["Workshop payment done",       closeModal.workshopPaymentStatus === "PAID"],
              ].map(([label, done]) => (
                <div key={label} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", borderRadius:8,
                  background: done ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)",
                  border: `1px solid ${done ? "rgba(34,197,94,.2)" : "rgba(239,68,68,.2)"}` }}>
                  <span style={{ fontSize:14 }}>{done ? "✅" : "⚠️"}</span>
                  <span style={{ fontSize:12, color: done ? "var(--wd-green)" : "var(--wd-red)", fontWeight: done ? 400 : 600 }}>{label}</span>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <label className="wd-form-label">Closing Remarks (optional)</label>
              <textarea className="wd-input" rows={2} style={{ resize:"vertical" }}
                placeholder="e.g. All payments complete. Vehicle delivered. Case closed."
                value={closeForm.closingRemarks}
                onChange={e => setCloseForm(f => ({ ...f, closingRemarks: e.target.value }))} />
            </div>
          </>
        )}
      </Modal>

      {/* 9. Claim Detail side panel */}
      {detailClaim && (
        <ClaimDetailModal
            claim={detailClaim}
            // onClose={() => setDetailClaim(null)}
            onClaimNo={() => { setClaimNoModal(detailClaim); setClaimNoForm({ claimNumber: detailClaim.claimNumber || "" }); setDetailClaim(null); }}
            onStatus={() => { openStatusModal(detailClaim); setDetailClaim(null); }}
            onAmounts={() => { openAmountModal(detailClaim, setAmountModal, setAmountForm); setDetailClaim(null); }}
            onNotify={() => { setNotifyModal(detailClaim); setDetailClaim(null); }}
            onPayRecv={() => { setPayRecvModal(detailClaim); setPayRecvForm({ receivedAmount:"", note:"", workshopPaid:false }); setDetailClaim(null); }}
            onPayWorkshop={() => { setPayWorkshopModal(detailClaim); setPayWorkshopForm({ amount: "", note: "" }); setDetailClaim(null);}}
            onCustPay={() => { setCustPayModal(detailClaim); setCustPayForm({ amount:String(detailClaim.customerDue || ""), mode:"CASH", note:"" }); setDetailClaim(null); }}
            onSettle={() => { setSettleModal(detailClaim); setSettleForm({ settlementAmount: String(detailClaim.paidAmount||detailClaim.insApprovedAmount||""), settlementDate:"", remarks:"" }); setDetailClaim(null); }}
            onClose={() => { setCloseModal(detailClaim); setCloseForm({ closingRemarks:"" }); setDetailClaim(null); }}
            onNavigate={navigate}
        />
      )}
    </div>
  );
}

// ─── Action Buttons ───────────────────────────────────────────────────────────
function ActionButtons({ claim, onClaimNo, onStatus, onAmounts, onNotify, onReject, onPayRecv, onPayWorkshop, onCustPay, onSettle, onClose }) {
  const s = claim.claimStatus;
  const custDone = customerPaymentDone(claim);
  console.log("claim----",claim);

  return (
    <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
      <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={onStatus}>Update Status</button>

      {!claim.claimNumber && (
        <button className="wd-btn wd-btn-sm" onClick={onClaimNo}
          style={{ background:"rgba(234,179,8,.12)", color:"var(--wd-yellow)", border:"1px solid rgba(234,179,8,.3)", fontSize:11 }}>
          + Claim No.
        </button>
      )}

      {(s === "APPROVED" || (s !== "NO_CLAIM" && !claim.insApprovedAmount && s !== "REJECTED")) && (
        <button className="wd-btn wd-btn-sm" onClick={onAmounts}
          style={{ background:"rgba(34,197,94,.1)", color:"var(--wd-green)", border:"1px solid rgba(34,197,94,.25)", fontSize:11 }}>
          Amounts
        </button>
      )}

      {claim.customerDue != null && !claim.customerNotified &&
       !["NO_CLAIM","REJECTED","CASE_CLOSED"].includes(s) && (
        <button className="wd-btn wd-btn-sm" onClick={onNotify}
          style={{ background:"rgba(249,115,22,.1)", color:"var(--wd-orange)", border:"1px solid rgba(249,115,22,.25)", fontSize:11 }}>
          📧 Notify
        </button>
      )}
      {claim.customerNotified && !["NO_CLAIM","REJECTED","CASE_CLOSED"].includes(s) && (
        <button className="wd-btn wd-btn-sm" onClick={onNotify}
          style={{ background:"rgba(20,184,166,.1)", color:"var(--wd-teal)", border:"1px solid rgba(20,184,166,.25)", fontSize:11 }}>
          📧 Resend
        </button>
      )}
        {/* Collect customer payment — show when: customerDue > 0, notified, not yet paid */}
      {claim.customerPaymentStatus === "PENDING" && claim.customerNotified && !custDone && !["NO_CLAIM","REJECTED","CASE_CLOSED"].includes(s) && (
        <button className="wd-btn wd-btn-sm" onClick={onCustPay}
          style={{ background:"rgba(249,115,22,.12)", color:"var(--wd-orange)", border:"1px solid rgba(249,115,22,.3)", fontSize:11, fontWeight:600 }}>
          💵 Collect
        </button>
      )}

      {s === "PAYMENT_REQUESTED" && (
        <button className="wd-btn wd-btn-sm" onClick={onPayRecv}
          style={{ background:"rgba(34,197,94,.1)", color:"var(--wd-green)", border:"1px solid rgba(34,197,94,.25)", fontSize:11 }}>
          ✓ Mark Received
        </button>
      )}

       {["PAYMENT_RECEIVED", "SETTLED"].includes(s) && claim.workshopPaymentStatus !== "PAID" && (
        <button className="wd-btn wd-btn-sm" onClick={onPayWorkshop}
          style={{ background:"rgba(234,179,8,.1)", color:"var(--wd-yellow)", border:"1px solid rgba(234,179,8,.3)", fontSize:11 }}>
          🏭 Pay Workshop
        </button>
      )}


      {s === "PAYMENT_RECEIVED" && (
        <button className="wd-btn wd-btn-sm" onClick={onSettle}
          style={{ background:"rgba(34,197,94,.1)", color:"var(--wd-green)", border:"1px solid rgba(34,197,94,.3)", fontSize:11 }}>
          ✓ Settle
        </button>
      )}
      

      {s === "SETTLED" && (
        <button className="wd-btn wd-btn-sm" onClick={onClose}
          style={{ background:"rgba(168,85,247,.1)", color:"var(--wd-purple)", border:"1px solid rgba(168,85,247,.3)", fontSize:11 }}>
          🔒 Close Case
        </button>
      )}

      {s === "REJECTED" && (
        <button className="wd-btn wd-btn-sm" onClick={onReject}
          style={{ background:"rgba(239,68,68,.1)", color:"var(--wd-red)", border:"1px solid rgba(239,68,68,.25)", fontSize:11 }}>
          View Reason
        </button>
      )}
    </div>
  );
}
